import React, { Fragment, useEffect, useState } from "react";
import {
  Loading,
  Title,
  useAuthenticated,
  useAuthProvider,
  usePermissions,
  useVersion,
} from "react-admin";
import "./Dashboard.css";
import { ProLogsPermissions } from "../../common/auth/permissions";
import permissionNames from "../../common/auth/access-control/permissionNames";
import { getFirstAdminResource } from "../../common/admin-wrapper/adminResources";
import { Redirect } from "react-router-dom";
import { ProLogsUserProfile } from "../../common/auth/Auth";
import { handleAxiosError } from "../../common/data/axiosErrorHandler";
import { dashboardPermissions } from ".";
import maintenanceDashboard from "../assets/maintenance-dashboard";
import { LocationsDashboard } from "../locations/LocationsDashboard";
import safetyDashboard from "../safety/safety-dashboard";

const selectDefaultDashboard = (version: number, defaultDashboard: string) => {
  switch (defaultDashboard) {
    case "maintenance":
      return (
        <Fragment key={version}>
          <maintenanceDashboard.list />
        </Fragment>
      );

    case "safety":
      return (
        <Fragment key={version}>
          <safetyDashboard.list />
        </Fragment>
      );

    default:
      return (
        <Fragment key={version}>
          <Title title={"menu.dashboard"} />
          <LocationsDashboard />
        </Fragment>
      );
  }
};

const selectDashboard = (
  version: number,
  defaultDashboard: string | undefined,
  actionPermissions: { [name: string]: boolean } | undefined
) => {
  if (defaultDashboard) {
    if (!actionPermissions) {
      return selectDefaultDashboard(version, defaultDashboard);
    }

    if (
      dashboardPermissions[defaultDashboard].every((x) => actionPermissions[x])
    ) {
      return selectDefaultDashboard(version, defaultDashboard);
    }
  }

  if (
    !actionPermissions ||
    !actionPermissions[permissionNames.locations.current.main]
  ) {
    const firstResource = getFirstAdminResource(actionPermissions);
    return <Redirect to={`/${firstResource}`} />;
  }

  return selectDefaultDashboard(version, "locations");
};

export const Dashboard = () => {
  useAuthenticated();
  const version = useVersion();
  const { permissions } = usePermissions<ProLogsPermissions>();
  const authProvider = useAuthProvider();
  const [defaultDashboard, setDefaultDashboard] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!authProvider) {
      setLoading(true);
      return;
    }

    if (!authProvider.getIdentity) {
      setLoading(false);
      return;
    }

    authProvider
      .getIdentity()
      .then((identity) => {
        const roleDashboard = (identity as Partial<ProLogsUserProfile>)
          .role_dashboard;
        if (roleDashboard) {
          setDefaultDashboard(roleDashboard);
        }
      })
      .catch((e) => console.error(handleAxiosError(e)))
      .finally(() => setLoading(false));
  }, [authProvider]);

  if (loading) {
    return <Loading />;
  }

  return selectDashboard(
    version,
    defaultDashboard,
    permissions?.actionPermissions
  );
};
